import { Eye, Shield, Zap } from "lucide-react";
import { motion } from "framer-motion";

const reasons = [
  {
    icon: Eye,
    number: "1",
    title: "They find you first on Google",
    description: "Your business appears before competitors in local search results — in the exact suburbs you want to work.",
  },
  {
    icon: Shield,
    number: "2",
    title: "You look more trustworthy",
    description: "Professional website + strong Google reviews = instant customer confidence before they even call.",
  },
  {
    icon: Zap,
    number: "3",
    title: "You reply faster than anyone",
    description: "Automated responses mean you win the job before competitors even check their missed calls.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

export function WhyWorksSection() {
  return (
    <section className="section-padding bg-background relative overflow-hidden" aria-labelledby="why-works-heading">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-primary/[0.03] rounded-full blur-[80px] sm:blur-[100px] translate-x-1/2 -translate-y-1/2" />
      <div className="absolute inset-0 grid-pattern opacity-25" />

      <div className="container-wide relative">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-6 sm:mb-8"
          >
            <div className="section-badge mb-3 sm:mb-4 text-xs">
              <span>Why Trade Businesses Choose Us</span>
            </div>
            <h2 id="why-works-heading" className="heading-section text-foreground mb-2 sm:mb-3 text-balance">
              How Australian Customers Choose Their Tradie:{" "}
              <span className="text-gradient">Visibility, Trust & Speed</span>
            </h2>
            <p className="text-[0.9375rem] sm:text-base text-muted-foreground max-w-xl mx-auto leading-[1.7]">
              What this means for your business — our system aligns with exactly how customers hire today:
            </p>
          </motion.div>

          {/* Three Pillars */}
          <div className="grid md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {reasons.map((reason, index) => (
              <motion.article
                key={reason.number}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="relative text-center bg-card rounded-xl p-5 sm:p-6 border border-border/50 shadow-card"
              >
                {/* Icon container */}
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3" aria-hidden="true">
                  <reason.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                
                {/* Number watermark */}
                <div className="absolute top-4 left-4 text-3xl font-bold text-primary/[0.06] leading-none" aria-hidden="true">
                  {reason.number}
                </div>
                
                <h3 className="text-[0.9375rem] sm:text-base font-bold text-foreground mb-2">
                  {reason.title}
                </h3>
                <p className="text-muted-foreground leading-[1.65] text-[0.9375rem] sm:text-base">
                  {reason.description}
                </p>
              </motion.article>
            ))}
          </div>

        {/* Bottom Statement with internal link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="bg-foreground rounded-xl sm:rounded-2xl px-6 sm:px-8 py-6 sm:py-8 text-center relative overflow-hidden shadow-xl"
        >
            <p className="text-[0.9375rem] sm:text-base text-background/75 mb-1.5">
              Most trade businesses fail at all three of these.
            </p>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-background">
              With our system, yours won't. <a href="#process-section" className="text-primary hover:underline text-base font-semibold">See how we work →</a>
          </p>
          {/* Green accent bar */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-1 bg-primary rounded-t-full" />
        </motion.div>
        </div>
      </div>
    </section>
  );
}
